/**
 * Custom hook for managing sourcing requests with API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sourcingRequestsAPI, customersAPI } from '@/lib/api';
import { useToast } from '@/hooks/useToast';

const API_BASE_URL = 'https://sourcemygoods.com/api/public';

// Helper function to send emails
async function sendEmail(endpoint: string, data: any) {
  try {
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      console.error(`Failed to send ${endpoint}:`, await response.text());
    }
  } catch (error) {
    console.error(`Error sending ${endpoint}:`, error);
  }
}

export function useSourcingRequests() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data, isLoading, error } = useQuery({
    queryKey: ['sourcing-requests'],
    queryFn: async () => {
      const response = await sourcingRequestsAPI.getAll();
      return response.data || [];
    },
  });

  const createMutation = useMutation({
    mutationFn: sourcingRequestsAPI.create,
    onSuccess: async (response: any, variables: any) => {
      queryClient.invalidateQueries({ queryKey: ['sourcing-requests'] });
      toast({ title: 'Success', description: 'Sourcing request submitted successfully' });

      // Send email notification to customer AND sales team
      try {
        // Fetch customer email
        const customersResponse = await customersAPI.getAll();
        const customers = customersResponse.data || [];
        const customer = customers.find((c: any) => c.code === variables.customer_code);

        if (customer && customer.email) {
          const requestNumber = response.data?.request_number || 'N/A';
          
          await sendEmail('send-request-created-email.php', {
            to: customer.email,
            customerName: customer.name || 'Valued Customer',
            requestData: {
              requestNumber: requestNumber,
              productName: variables.product_name || 'N/A',
              description: variables.description || '',
              quantity: variables.quantity || 0,
              quantityUnit: variables.quantity_unit || 'pcs',
              targetPrice: variables.target_price || null,
              specialRequirements: variables.special_requirements || '',
              expectedDeliveryDate: variables.expected_delivery_date || 'TBD',
              items: variables.items || [],
            },
          });
        }
      } catch (emailError) {
        console.error('Failed to send request created email:', emailError);
        // Don't show error to user - email is optional
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: sourcingRequestsAPI.update,
    onSuccess: async (response: any, variables: any) => {
      queryClient.invalidateQueries({ queryKey: ['sourcing-requests'] });
      toast({ title: 'Success', description: 'Sourcing request updated successfully' });

      // Send email if status changed
      if (variables.status) {
        try {
          // Wait for database to sync
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Get the request to find customer code
          const requestsResponse = await sourcingRequestsAPI.getAll();
          const requests = requestsResponse.data || [];
          
          const request = requests.find((r: any) => 
            r.request_number === variables.request_number || 
            r.requestNumber === variables.request_number
          );

          if (request) {
            // Fetch customer email
            const customersResponse = await customersAPI.getAll();
            const customers = customersResponse.data || [];
            
            const customer = customers.find((c: any) => 
              c.code === request.customer_code || c.code === request.customerCode
            );

            if (customer && customer.email) {
              // Calculate total item count for multi-item requests
              const items = request.items || [];
              const parsedItems = typeof items === 'string' ? JSON.parse(items || '[]') : items;
              const totalQuantity = parsedItems.length > 0 
                ? parsedItems.reduce((sum: number, item: any) => sum + (parseInt(item.quantity) || 0), 0)
                : (request.quantity || 0);
              
              // Send request status update email
              await sendEmail('send-request-status-email.php', {
                to: customer.email,
                customerName: customer.name || 'Valued Customer',
                requestNumber: variables.request_number,
                newStatus: variables.status,
                productName: parsedItems.length > 0 
                  ? `${parsedItems.length} item${parsedItems.length !== 1 ? 's' : ''}`
                  : (request.product_name || request.productName),
                quantity: totalQuantity,
                quantityUnit: parsedItems.length > 0 ? 'items' : (request.quantity_unit || request.quantityUnit || 'pcs'),
                grandTotal: request.grand_total || request.grandTotal || null,
                quoteValidUntil: request.quote_valid_until || request.quoteValidUntil || null,
              });
            }
          }
        } catch (emailError) {
          console.error('Failed to send request status email:', emailError);
        }
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: sourcingRequestsAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sourcing-requests'] });
      toast({ title: 'Success', description: 'Sourcing request deleted successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  return {
    sourcingRequests: data || [],
    isLoading,
    error,
    createSourcingRequest: createMutation.mutate,
    updateSourcingRequest: updateMutation.mutate,
    deleteSourcingRequest: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
